import type { ResponsePrepTime, VideoPlayerConfig } from "@memorang/types";
import { useRef } from "react";
import { useDevice } from "../../../../../useDevice";
import AudioPlayerContainer, {
	type AudioPlayerRef,
} from "../../../../AudioPlayerContainer";
import AudioRecorderContainer, {
	type AudioRecorderContainerRef,
	type RecordingLimitAlertProps,
} from "../../../../AudioRecorderContainer";
import Box from "../../../../Box";
import {
	SessionVideoPlayer,
	type SessionVideoPlayerRef,
} from "../../../../SessionVideoPlayer";
import { isAutoRecordMode } from "../../../helpers";
import useAudioResponseCompletion from "../../../hooks/useAudioResponseCompletion";
import useBeep from "../../../hooks/useBeep";
import usePrepSequence from "../../../hooks/usePrepSequence";
import type {
	RegisterActiveResponseCompletion,
	SessionAudioPlayerTrackingHandler,
	SessionCaptureError,
	SessionRecorderTrackingHandler,
} from "../../../types";
import { ItemInstruction } from "../pte/ItemInstruction";
import { ItemStemCard } from "../pte/ItemStemCard";
import { PrepCountdownBanner } from "../pte/PrepCountdownBanner";
import { SubInstruction } from "../pte/SubInstruction";
import { usePrepTimerLabel } from "../pte/usePrepTimerLabel";

type Props = {
	instruction: string;
	audioUri: string;
	videoUri?: string;
	audio: string;
	handleOnRecordingComplete: (uri: string | undefined) => void;
	recordingLimitAlert?: RecordingLimitAlertProps;
	subInstruction?: string;
	captureError: SessionCaptureError;
	handleTracking: SessionRecorderTrackingHandler;
	handleAudioPlayerTracking?: SessionAudioPlayerTrackingHandler;
	stem?: string;
	disabled?: boolean;
	prepTime?: ResponsePrepTime;
	timeLimit?: number;
	initialElapsedSeconds?: number;
	allowAudioReplay?: boolean;
	allowVideoReplay?: boolean;
	videoPlayerConfig?: VideoPlayerConfig;
	progressSeekable?: boolean;
	allowStopWhileRecording?: boolean;
	onResponseComplete?: () => void;
	onAudioEnded?: () => void;
	registerActiveResponseCompletion?: RegisterActiveResponseCompletion;
	onLimitReachedChange?: (reached: boolean) => void;
	onRecordingActiveChange?: (active: boolean) => void;
	onPlaybackDurationChange?: (durationMillis: number) => void;
};

const AudioRecordingItem = ({
	instruction,
	audioUri,
	videoUri,
	audio,
	handleOnRecordingComplete,
	recordingLimitAlert,
	subInstruction,
	captureError,
	handleTracking,
	handleAudioPlayerTracking,
	stem,
	disabled,
	prepTime,
	timeLimit,
	initialElapsedSeconds,
	allowAudioReplay,
	allowVideoReplay,
	videoPlayerConfig,
	progressSeekable,
	allowStopWhileRecording,
	onResponseComplete,
	onAudioEnded,
	registerActiveResponseCompletion,
	onLimitReachedChange,
	onRecordingActiveChange,
	onPlaybackDurationChange,
}: Props) => {
	const { isMobile } = useDevice();
	const audioPlayerRef = useRef<AudioPlayerRef | null>(null);
	const videoPlayerRef = useRef<SessionVideoPlayerRef | null>(null);
	const recorderRef = useRef<AudioRecorderContainerRef | null>(null);
	const isAutoRecord = isAutoRecordMode(prepTime, timeLimit);
	const playOnce = !allowAudioReplay;
	const videoPlayOnce = !allowVideoReplay;
	const beep = useBeep();
	const formatPrepLabel = usePrepTimerLabel();

	const { handleRecordingEnded, handleRecordingComplete } =
		useAudioResponseCompletion({
			recorderRef,
			onRecordingComplete: handleOnRecordingComplete,
			isAutoRecord,
			onRecordingEnded: beep.play,
			onResponseComplete,
			registerActiveResponseCompletion,
		});

	const { phase, remainingPrepSeconds, showBanner } = usePrepSequence({
		prepTime,
		timeLimit,
		hasAudioStimulus: Boolean(audioUri),
		hasVideoStimulus: Boolean(videoUri),
		hasRecorder: true,
		initialElapsedSeconds,
		audioPlayerRef,
		videoPlayerRef,
		recorderRef,
		onAudioEnded,
		onRecordingStarted: beep.play,
		onRecordingEnded: handleRecordingEnded,
		enabled: isAutoRecord && !(disabled || audio),
		waitForPlayer: true,
	});

	const isPrepCountdown = phase === "micDelay";

	return (
		<Box gap={isMobile ? 16 : 24}>
			<Box gap={8}>
				<ItemInstruction instruction={instruction} />
				{subInstruction ? <SubInstruction text={subInstruction} /> : null}
				{showBanner ? (
					<PrepCountdownBanner
						remainingSeconds={remainingPrepSeconds}
						playOnce={videoUri ? videoPlayOnce : playOnce}
						isVideo={Boolean(videoUri)}
					/>
				) : null}
				{videoUri ? (
					<SessionVideoPlayer
						ref={videoPlayerRef}
						uri={videoUri}
						playOnce={videoPlayOnce}
						videoPlayerConfig={videoPlayerConfig}
						progressSeekable={progressSeekable}
					/>
				) : null}
				{audioUri ? (
					<AudioPlayerContainer
						ref={audioPlayerRef}
						uri={audioUri}
						handleTracking={handleAudioPlayerTracking}
						playOnce={playOnce}
						playOnceInteractive={Boolean(disabled)}
					/>
				) : null}
			</Box>
			{stem ? <ItemStemCard text={stem} /> : null}
			<AudioRecorderContainer
				ref={recorderRef}
				recordedAudio={audio}
				handleOnRecordingComplete={handleRecordingComplete}
				recordingLimitAlert={recordingLimitAlert}
				captureError={captureError}
				handleTracking={handleTracking}
				onLimitReachedChange={onLimitReachedChange}
				onRecordingActiveChange={onRecordingActiveChange}
				onPlaybackDurationChange={onPlaybackDurationChange}
				disabled={disabled}
				type={isAutoRecord ? "autoRecord" : "default"}
				showDiscardButton={!isAutoRecord}
				allowStopWhileRecording={allowStopWhileRecording}
				maxDuration={timeLimit}
				remainingSecondsOverride={
					isPrepCountdown ? remainingPrepSeconds : undefined
				}
				formatTimerLabel={isPrepCountdown ? formatPrepLabel : undefined}
			/>
		</Box>
	);
};

export default AudioRecordingItem;
